export * from './app-shell-main-scroll.util';
export * from './catalog-label.util';
export * from './parse-api-error-message.util';
export * from '../utils/timer';
export * from './date-validators.util';
export * from './download-blob.util';
export * from './environment-banner.util';
export * from './extract-original-name.util';
export * from './finance-method-label.util';
export * from './first-name-segment.util';
export * from './signed-url.util';
export * from './inspection-terminal-status.util';
export * from './keyboard.util';
export * from './map-with-concurrency.util';
export * from './round-half-up.util';
export * from './cpf-duplicate.util';
export * from './i4pro-email-chars.util';
export * from './normalize-email.util';
export * from './zod-field-validator.util';
export {
  f,
  useDebouncedFilters,
  useDebouncedValue,
  useFilters,
} from './use-filters.hook';

export type { FilterFieldProps } from './use-filters.hook';

export function hasNumber(value: string): boolean {
  return /\d/.test(value);
}

export function hasUppercase(value: string): boolean {
  return /[A-ZÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ]/.test(value);
}

export function hasLowercase(value: string): boolean {
  return /[a-záàâãéèêíìîóòôõúùûç]/.test(value);
}

export function validateCpf(raw: string): boolean {
  const cpf = (raw || '').replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  const calc = (base: number) => {
    let sum = 0;
    for (let i = 0; i < base; i++) sum += parseInt(cpf[i], 10) * (base + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  const d1 = calc(9);
  const d2 = calc(10);
  return d1 === parseInt(cpf[9], 10) && d2 === parseInt(cpf[10], 10);
}

export {
  cleanCnpjOrCpf,
  formatCaepf,
  formatCnpj,
  formatCpf,
} from './document.util';

/**
 * Valor numérico de um caractere para o cálculo dos DVs do CNPJ alfanumérico
 * (regra da Receita Federal: ASCII − 48). Dígitos '0'..'9' mapeiam para 0..9 e
 * letras 'A'..'Z' para 17..42. Assim o algoritmo mod-11 funciona idêntico
 * tanto para CNPJ numérico (legado) quanto alfanumérico (novo formato RFB).
 */
function cnpjCharValue(c: string): number {
  return c.charCodeAt(0) - 48;
}

/**
 * Valida CNPJ no novo formato alfanumérico da Receita Federal:
 *  - 14 caracteres totais.
 *  - 12 primeiros: alfanuméricos (A-Z, 0-9). O numérico legado é subset.
 *  - 2 últimos: dígitos verificadores (DV13/DV14) calculados via mod-11.
 *
 * Aceita string com ou sem máscara — qualquer separador (pontos, barras, hífen,
 * espaços) é removido antes da validação. Letras minúsculas são normalizadas
 * para maiúsculas.
 */
export function validateCnpj(raw: string): boolean {
  const cnpj = (raw || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (cnpj.length !== 14) return false;
  // Os 12 primeiros não podem ser todos iguais (regra anti-trivial).
  if (/^(.)\1{11}/.test(cnpj.substring(0, 12))) return false;
  // Os 2 últimos (DVs) precisam ser dígitos numéricos.
  if (!/^\d{2}$/.test(cnpj.substring(12))) return false;

  const calcDigit = (base: string, weights: number[]) => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
      sum += cnpjCharValue(base[i]) * weights[i];
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const d1 = calcDigit(cnpj.substring(0, 12), weights1);
  const d2 = calcDigit(cnpj.substring(0, 13), weights2);

  return d1 === parseInt(cnpj[12], 10) && d2 === parseInt(cnpj[13], 10);
}

/**
 * Mensagem de erro ou undefined se CPF (11 dígitos) ou CNPJ (14 alfanuméricos)
 * for válido. CNPJ aceita o novo formato alfanumérico da Receita Federal — CPF
 * continua estritamente numérico.
 */
export function validateRegisterNumberDocument(
  value: string | undefined
): string | undefined {
  const raw = (value || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (!raw) return 'CPF/CNPJ é obrigatório';

  // Heurística por tamanho: 1..11 chars → CPF (numérico); 12..14 → CNPJ
  // (alfanumérico). Se vier letra num documento de 11 chars, rejeita como
  // CPF inválido (CPF não tem letras).
  if (raw.length <= 11) {
    if (raw.length < 11) return 'CPF/CNPJ incompleto';
    if (!/^\d{11}$/.test(raw)) return 'CPF inválido';
    return validateCpf(value!) ? undefined : 'CPF inválido';
  }
  if (raw.length < 14) return 'CNPJ incompleto';
  if (raw.length > 14) return 'CPF/CNPJ inválido';
  return validateCnpj(value!) ? undefined : 'CNPJ inválido';
}

/**
 * Validação de CAEPF (Cadastro de Atividade Econômica de Pessoa Física).
 *
 * CAEPF tem 14 dígitos como CNPJ, mas **não possui dígitos verificadores
 * com algoritmo público** — apenas length + sanity check (não todos iguais).
 * A validade real é confirmada apenas pela consulta à Receita Federal
 * (via Compliance.Api / Diretrix).
 */
export function validateCaepf(raw: string): boolean {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  return true;
}

export function validatePhone(raw: string): boolean {
  const phone = (raw || '').replace(/\D/g, '');

  if (phone.length === 0) return true;

  if (phone.length !== 10 && phone.length !== 11) return false;

  if (/^(\d)\1+$/.test(phone)) return false;

  const ddd = parseInt(phone.substring(0, 2), 10);

  if (ddd < 11 || ddd > 99) return false;

  if (phone.length === 11) {
    const thirdDigit = parseInt(phone[2], 10);
    if (thirdDigit !== 9) return false;
  }

  return true;
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function parsePercent(value?: string): number | null {
  const raw = (value || '').toString().trim();
  if (!raw) return null;
  const normalized = raw
    .replace(/[^0-9,.]/g, '')
    .replace(/\.(?=\d{3,})/g, '')
    .replace(',', '.');
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : null;
}

export {
  useAdvisoryOptions,
  useAdvisoryOptionLabel,
} from './advisory-options.util.ts';
export type { OptionItem } from './advisory-options.util.ts';
export { checkPermissions, requirePermissions } from './check-permissions.util';
export { getUserCognitoGroups } from './auth-groups.util';
export {
  isCurrentUserCommercialInsurer,
  requireInsurerUnderwritingAccess,
} from './require-insurer-underwriting-access.util';
export { requireAdministratorAccess } from './require-administrator-access.util';
export { requireFinancialAccess } from './require-financial-access.util';
export { requireNotificationsAccess } from './require-notifications-access.util';
export {
  formatCurrency,
  formatCurrencyFromString,
  getFormattedCurrency,
} from './currencyFormatter';
export {
  BRAZIL_TIME_ZONE,
  formatDateToBrazil,
  formatDateTimeToBrazil,
  formatDateTimeShortToBrazil,
} from './format-date.util';
export { formatRegisterNumber } from './format-register-number';
export { formatPartyDocument } from './format-party-document.util';
